import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { 
  FileText, 
  Package, 
  MessageSquare, 
  Upload, 
  BarChart3, 
  TrendingUp,
  Users,
  Database,
  Bot,
  Activity,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  Zap,
  Settings,
  TestTube
} from 'lucide-react';

const Dashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [recentChats, setRecentChats] = useState([]);
  const [systemStatus, setSystemStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [aiEngineStatus, setAiEngineStatus] = useState({
    openai: { status: 'checking', message: 'Checking...', lastChecked: null },
    anthropic: { status: 'checking', message: 'Checking...', lastChecked: null }
  });

  useEffect(() => {
    fetchDashboardData();
    checkAIEngineStatus();
  }, []);

  const checkAIEngineStatus = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers || Object.keys(headers).length === 0) {
        console.warn('No auth headers available for AI status check');
        setAiEngineStatus(prev => ({
          openai: { ...prev.openai, status: 'error', message: 'Please login with API key' },
          anthropic: { ...prev.anthropic, status: 'error', message: 'Please login with API key' }
        }));
        return;
      }

      // Check AI engine status (both OpenAI and Anthropic in one call)
      const statusResponse = await fetch(`${API_BASE_URL}/api/v1/config/ai/status`, { headers });
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        console.log('AI status response:', statusData);
        
        // Update OpenAI status
        if (statusData.openai) {
          const isHealthy = statusData.openai.configured && statusData.openai.status === 'connected';
          setAiEngineStatus(prev => ({
            ...prev,
            openai: {
              status: isHealthy ? 'healthy' : 'error',
              message: statusData.openai.message || 'Not configured',
              lastChecked: new Date().toLocaleTimeString()
            }
          }));
        }
        
        // Update Anthropic status
        if (statusData.anthropic) {
          const isHealthy = statusData.anthropic.configured && statusData.anthropic.status === 'connected';
          setAiEngineStatus(prev => ({
            ...prev,
            anthropic: {
              status: isHealthy ? 'healthy' : 'error',
              message: statusData.anthropic.message || 'Not configured',
              lastChecked: new Date().toLocaleTimeString()
            }
          }));
        }
      } else {
        console.error('AI status check failed:', statusResponse.status, statusResponse.statusText);
        setAiEngineStatus(prev => ({
          openai: { ...prev.openai, status: 'error', message: `HTTP ${statusResponse.status}` },
          anthropic: { ...prev.anthropic, status: 'error', message: `HTTP ${statusResponse.status}` }
        }));
      }
    } catch (error) {
      console.error('Error checking AI engine status:', error);
      setAiEngineStatus(prev => ({
        openai: { ...prev.openai, status: 'error', message: 'Connection failed' },
        anthropic: { ...prev.anthropic, status: 'error', message: 'Connection failed' }
      }));
    }
  };

  const testAIEngine = async (provider) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      // Update status to testing
      setAiEngineStatus(prev => ({
        ...prev,
        [provider]: { status: 'checking', message: 'Testing...', lastChecked: new Date().toLocaleTimeString() }
      }));

      // Make test call
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai/test`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          engine_type: provider,
          api_key: 'test', // This will be validated on the backend
          model_name: 'test'    // This will be validated on the backend
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAiEngineStatus(prev => ({
            ...prev,
            [provider]: { 
              status: 'healthy', 
              message: 'Working correctly', 
              lastChecked: new Date().toLocaleTimeString() 
            }
          }));
        } else {
          setAiEngineStatus(prev => ({
            ...prev,
            [provider]: { 
              status: 'error', 
              message: data.message || 'Test failed', 
              lastChecked: new Date().toLocaleTimeString() 
            }
          }));
        }
      } else {
        setAiEngineStatus(prev => ({
          ...prev,
          [provider]: { 
            status: 'error', 
            message: 'Test failed', 
            lastChecked: new Date().toLocaleTimeString() 
          }
        }));
      }
    } catch (error) {
      console.error(`Error testing ${provider}:`, error);
      setAiEngineStatus(prev => ({
        ...prev,
        [provider]: { 
          status: 'error', 
          message: 'Connection failed', 
          lastChecked: new Date().toLocaleTimeString() 
        }
      }));
    }
  };

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return; // Exit if not authenticated
      
      // Fetch document stats
      const docStatsResponse = await fetch(`${API_BASE_URL}/api/v1/documents/stats/overview`, { headers });
      const docStats = await docStatsResponse.json();
      
      // Fetch product stats
      const productStatsResponse = await fetch(`${API_BASE_URL}/api/v1/products/stats/overview`, { headers });
      const productStats = await productStatsResponse.json();
      
      // Fetch system health
      const healthResponse = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, { headers });
      const health = await healthResponse.json();
      
      // Fetch recent documents
      const recentDocsResponse = await fetch(`${API_BASE_URL}/api/v1/documents/?limit=5`, { headers });
      const recentDocs = await recentDocsResponse.json();
      
      // Fetch recent chat sessions
      const recentChatsResponse = await fetch(`${API_BASE_URL}/api/v1/chat/sessions?limit=5`, { headers });
      const recentChats = await recentChatsResponse.json();

      setStats({
        documents: docStats,
        products: productStats
      });
      setRecentDocuments(recentDocs.documents || []);
      setRecentChats(recentChats.sessions || []);
      setSystemStatus(health);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle size={16} className="text-success" />;
      case 'warning':
        return <AlertTriangle size={16} className="text-warning" />;
      case 'error':
        return <AlertTriangle size={16} className="text-danger" />;
      default:
        return <Info size={16} className="text-info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-danger';
      default:
        return 'text-info';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h3 mb-1">Dashboard</h2>
          <p className="text-muted mb-0">Welcome to AI Tech Assistant - Overview and Analytics</p>
        </div>
        <div className="d-flex gap-2">
          <Link to="/documents" className="btn btn-primary">
            <Upload size={18} className="me-2" />
            Upload Document
          </Link>
          <Link to="/chat" className="btn btn-outline-primary">
            <MessageSquare size={18} className="me-2" />
            Start Chat
          </Link>
        </div>
      </div>

      {/* System Status */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Activity size={20} className="text-primary" />
                System Status
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="d-flex align-items-center gap-2">
                    {getStatusIcon(systemStatus.components?.database?.status)}
                    <span className={getStatusColor(systemStatus.components?.database?.status)}>
                      Database: {systemStatus.components?.database?.status || 'Unknown'}
                    </span>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="d-flex align-items-center gap-2">
                    {getStatusIcon(systemStatus.components?.ai_services?.openai?.available ? 'healthy' : 'error')}
                    <span className={systemStatus.components?.ai_services?.openai?.available ? 'text-success' : 'text-danger'}>
                      OpenAI: {systemStatus.components?.ai_services?.openai?.available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="d-flex align-items-center gap-2">
                    {getStatusIcon(systemStatus.components?.ai_services?.claude?.available ? 'healthy' : 'error')}
                    <span className={systemStatus.components?.ai_services?.claude?.available ? 'text-success' : 'text-danger'}>
                      Claude: {systemStatus.components?.ai_services?.claude?.available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="d-flex align-items-center gap-2">
                    {getStatusIcon(systemStatus.status)}
                    <span className={getStatusColor(systemStatus.status)}>
                      Overall: {systemStatus.status || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Engine Status */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <TestTube size={20} className="text-primary" />
                AI Engine Status
              </h6>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={checkAIEngineStatus}
                title="Refresh AI engine status"
              >
                <Activity size={16} className="me-1" />
                Refresh
              </button>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      {getStatusIcon(aiEngineStatus.openai.status)}
                      <span className={getStatusColor(aiEngineStatus.openai.status)}>
                        <strong>OpenAI:</strong> {aiEngineStatus.openai.message}
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => testAIEngine('openai')}
                        disabled={aiEngineStatus.openai.status === 'checking'}
                      >
                        {aiEngineStatus.openai.status === 'checking' ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : (
                          <TestTube size={14} className="me-1" />
                        )}
                        Test
                      </button>
                      <small className="text-muted">
                        {aiEngineStatus.openai.lastChecked}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      {getStatusIcon(aiEngineStatus.anthropic.status)}
                      <span className={getStatusColor(aiEngineStatus.anthropic.status)}>
                        <strong>Anthropic:</strong> {aiEngineStatus.anthropic.message}
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => testAIEngine('anthropic')}
                        disabled={aiEngineStatus.anthropic.status === 'checking'}
                      >
                        {aiEngineStatus.anthropic.status === 'checking' ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : (
                          <TestTube size={14} className="me-1" />
                        )}
                        Test
                      </button>
                      <small className="text-muted">
                        {aiEngineStatus.anthropic.lastChecked}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Total Documents</h6>
                  <h3 className="mb-0 fw-bold text-primary">
                    {stats?.documents?.total_documents || 0}
                  </h3>
                </div>
                <div className="bg-primary-subtle rounded-circle p-3">
                  <FileText size={24} className="text-primary" />
                </div>
              </div>
              <div className="mt-3">
                <span className="badge bg-success-subtle text-success">
                  <TrendingUp size={14} className="me-1" />
                  +12% this week
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Products</h6>
                  <h3 className="mb-0 fw-bold text-success">
                    {stats?.products?.total_products || 0}
                  </h3>
                </div>
                <div className="bg-success-subtle rounded-circle p-3">
                  <Package size={24} className="text-success" />
                </div>
              </div>
              <div className="mt-3">
                <span className="badge bg-info-subtle text-info">
                  <Package size={14} className="me-1" />
                  {stats?.products?.products_with_documents || 0} with docs
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Storage Used</h6>
                  <h3 className="mb-0 fw-bold text-warning">
                    {stats?.documents?.total_size_mb || 0} MB
                  </h3>
                </div>
                <div className="bg-warning-subtle rounded-circle p-3">
                  <Database size={24} className="text-warning" />
                </div>
              </div>
              <div className="mt-3">
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-warning" 
                    style={{ width: `${Math.min((stats?.documents?.total_size_mb || 0) / 1024 * 100, 100)}%` }}
                  ></div>
                </div>
                <small className="text-muted">
                  {((stats?.documents?.total_size_mb || 0) / 1024 * 100).toFixed(1)}% of 1GB limit
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">AI Queries</h6>
                  <h3 className="mb-0 fw-bold text-info">
                    {recentChats.length}
                  </h3>
                </div>
                <div className="bg-info-subtle rounded-circle p-3">
                  <Bot size={24} className="text-info" />
                </div>
              </div>
              <div className="mt-3">
                <span className="badge bg-primary-subtle text-primary">
                  <MessageSquare size={14} className="me-1" />
                  Recent sessions
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="row">
        {/* Recent Documents */}
        <div className="col-lg-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <FileText size={20} className="text-primary" />
                Recent Documents
              </h6>
              <Link to="/documents" className="btn btn-sm btn-outline-primary">
                View All
              </Link>
            </div>
            <div className="card-body">
              {recentDocuments.length > 0 ? (
                <div className="list-group list-group-flush">
                  {recentDocuments.map((doc) => (
                    <div key={doc.id} className="list-group-item border-0 px-0 py-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-light rounded-circle p-2">
                            <FileText size={16} className="text-muted" />
                          </div>
                          <div>
                            <h6 className="mb-1 small fw-semibold">{doc.original_filename}</h6>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-secondary-subtle text-secondary">
                                {doc.file_type.toUpperCase()}
                              </span>
                              <span className="small text-muted">
                                {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">
                            <Clock size={14} className="me-1" />
                            {new Date(doc.uploaded_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FileText size={48} className="text-muted mb-3" />
                  <p className="text-muted mb-0">No documents uploaded yet</p>
                  <Link to="/documents" className="btn btn-sm btn-primary mt-2">
                    Upload First Document
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Chat Sessions */}
        <div className="col-lg-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <MessageSquare size={20} className="text-primary" />
                Recent Chat Sessions
              </h6>
              <Link to="/chat" className="btn btn-sm btn-outline-primary">
                Start New Chat
              </Link>
            </div>
            <div className="card-body">
              {recentChats.length > 0 ? (
                <div className="list-group list-group-flush">
                  {recentChats.map((chat) => (
                    <div key={chat.session_id} className="list-group-item border-0 px-0 py-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-light rounded-circle p-2">
                            <MessageSquare size={16} className="text-muted" />
                          </div>
                          <div>
                            <h6 className="mb-1 small fw-semibold">
                              {chat.title || `Session ${chat.session_id.slice(0, 8)}...`}
                            </h6>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-info-subtle text-info">
                                {chat.message_count} messages
                              </span>
                              <span className="small text-muted">
                                <Users size={14} className="me-1" />
                                ID: {chat.id}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">
                            <Clock size={14} className="me-1" />
                            {new Date(chat.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <MessageSquare size={48} className="text-muted mb-3" />
                  <p className="text-muted mb-0">No chat sessions yet</p>
                  <Link to="/chat" className="btn btn-sm btn-primary mt-2">
                    Start First Chat
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Zap size={20} className="text-primary" />
                Quick Actions
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <Link to="/documents" className="card border-0 shadow-sm text-decoration-none h-100">
                    <div className="card-body text-center p-4">
                      <div className="bg-primary-subtle rounded-circle p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                        <Upload size={24} className="text-primary" />
                      </div>
                      <h6 className="fw-semibold">Upload Document</h6>
                      <p className="small text-muted mb-0">Add new documents to your knowledge base</p>
                    </div>
                  </Link>
                </div>
                
                <div className="col-md-3">
                  <Link to="/products" className="card border-0 shadow-sm text-decoration-none h-100">
                    <div className="card-body text-center p-4">
                      <div className="bg-success-subtle rounded-circle p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                        <Package size={24} className="text-success" />
                      </div>
                      <h6 className="fw-semibold">Manage Products</h6>
                      <p className="small text-muted mb-0">Organize documents by products</p>
                    </div>
                  </Link>
                </div>
                
                <div className="col-md-3">
                  <Link to="/chat" className="card border-0 shadow-sm text-decoration-none h-100">
                    <div className="card-body text-center p-4">
                      <div className="bg-info-subtle rounded-circle p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                        <MessageSquare size={24} className="text-info" />
                      </div>
                      <h6 className="fw-semibold">Start Chat</h6>
                      <p className="small text-muted mb-0">Query your documents with AI</p>
                    </div>
                  </Link>
                </div>
                
                <div className="col-md-3">
                  <Link to="/configurations" className="card border-0 shadow-sm text-decoration-none h-100">
                    <div className="card-body text-center p-4">
                      <div className="bg-warning-subtle rounded-circle p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                        <Settings size={24} className="text-warning" />
                      </div>
                      <h6 className="fw-semibold">Configure</h6>
                      <p className="small text-muted mb-0">Set up AI providers and system settings</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-primary-subtle { background-color: rgba(13, 110, 253, 0.1) !important; }
        .bg-success-subtle { background-color: rgba(25, 135, 84, 0.1) !important; }
        .bg-warning-subtle { background-color: rgba(255, 193, 7, 0.1) !important; }
        .bg-info-subtle { background-color: rgba(13, 202, 240, 0.1) !important; }
        .bg-secondary-subtle { background-color: rgba(108, 117, 125, 0.1) !important; }
        
        .card {
          transition: all 0.3s ease;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
        
        .list-group-item:hover {
          background-color: #f8f9fa;
        }
        
        .progress {
          background-color: #e9ecef;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
